import { Typography } from '@navi/web-ui/lib/primitives';
import styles from '../../AgentForm.module.scss';
import AgentFormTextbox from './AgentFormTextbox';
import {
  ENTITY_TYPE,
  NAVI_AGENCY_CODE,
  VALIDATION_MESSAGE,
  VERIFICATION_MESSAGE
} from '../../constants/constants';
import AgentFormDropdown from './AgentFormDropdown';
import AgentFormAutocompleteDropdown from './AgentFormAutocompleteDropdown';
import { useSelector } from 'react-redux';
import { RootState } from '@cp/src/store';
import { convertStringToDropdownOption, convertToDropdownOptions } from '../../utils/utils';
import AgentFormDownloadDraCertificate from './AgentFormDownloadDraCertificate';
import AgentFormUploadControl from './AgentFormUploadControl';
import AgentBaseLocation from './AgentBaseLocation';
import AgentFormRadioButton from './AgentFormRadioButton';
import VerificationComponent from './VerificationComponent';
import { useState } from 'react';
import WarningSection from './WarningSection';
import {
  handleLocationCoordinatesChange,
  handleVerification
} from '../../actions/agentFormActions';
import { IAgentFormProps } from '../../interfaces/types';
import AgentFormDownloadPan from './AgentFormDownloadPan';
import AgentFormDateInput from './AgentFormDateInput';
import FormToggleTab from '@cp/src/pages/AgencyManagement/components/forms/FormToggleTab';
import { tabOptions } from '@cp/src/pages/AgencyManagement/constants/constants';
import ActiveNextMonthWrapper from './ActiveNextMonthWrapper';
import CorporateNumbersDrawer from './CorporateNumbersDrawer';

const InternalFieldAgentForm = (props: IAgentFormProps) => {
  const {
    control,
    errors,
    isEditDisabled,
    watch,
    isEmailRequired,
    validateEmail,
    isNaviEmailRequired,
    isEmailChangeDisabled,
    agentData,
    getValues,
    getAgencyCode,
    managerList,
    setValue,
    isDraUploadAccessFeatureFlagEnabled,
    setIsSubmitButtonDisabled,
    reverseGeocodedAgentCity,
    reverseGeocodedAgentPincode,
    locationVerificationStatus,
    setLocationVerificationStatus,
    showLocationVerificationToast,
    setReverseGeocodedAgentCity,
    setReverseGeocodedAgentPincode,
    setShowLocationVerificationToast,
    isEditPanDetailsDisabled = false,
    showActiveNextMonthToggle = false,
    isAgentActiveNextMonth,
    setIsAgentActiveNextMonth,
    isAgentActiveNextMonthToggleDirty,
    setIsAgentActiveNextMonthToggleDirty
  } = props;

  const agentPreferences = useSelector(
    (state: RootState) => state?.teamLeadDashboard?.agentPreferences
  );
  const bucketGroup = useSelector((state: RootState) => state?.teamLeadDashboard?.bucketGroup);
  const AGENT_CITY = agentPreferences?.cityList || [];
  const BUCKET_GROUPS = bucketGroup || [];
  const panVerificationStatus = agentData?.panCardView?.userDocument?.status || '';
  const [isCityMismatch, setIsCityMismatch] = useState(false);
  const [showVerifyButton, setShowVerifyButton] = useState(false);
  const [showCityPincode, setShowCityPincode] = useState(
    !!reverseGeocodedAgentCity && !!reverseGeocodedAgentPincode
  );
  const [isMisMatchedCity, setMisMatchedCity] = useState(false);

  const handleCityVerification = async () => {
    handleVerification({
      getValues,
      setLocationVerificationStatus,
      setShowLocationVerificationToast,
      setShowCityPincode,
      setReverseGeocodedAgentCity,
      setReverseGeocodedAgentPincode,
      setMisMatchedEntity: setMisMatchedCity,
      entity: ENTITY_TYPE.CITY
    });
  };

  const handleAgentCityChange = (value: string) => {
    setMisMatchedCity(false);
    if (
      reverseGeocodedAgentCity &&
      value &&
      value.toLowerCase() !== reverseGeocodedAgentCity.toLowerCase()
    ) {
      setIsCityMismatch(true);
    } else {
      setIsCityMismatch(false);
    }
  };

  const handleCoordinatesChange = (value: string) => {
    handleLocationCoordinatesChange({
      setIsEntityMismatch: setIsCityMismatch,
      setLocationVerificationStatus,
      setShowVerifyButton,
      setShowCityPincode,
      setShowLocationVerificationToast
    });
  };

  return (
    <>
      <div className={'pt-4 pl-5 mt-4'}>
        <Typography variant={'h4'} color="var(--navi-color-gray-c2)">
          Agent Details
        </Typography>
      </div>
      <div className={styles.inputContainer}>
        <div className="flex flex-row justify-between items-center pr-8">
          <div className="flex flex-row gap-1">
            <Typography
              variant="p4"
              color="var(--navi-bordered-input-label-text-color)"
              className="pt-1.5"
            >
              Training Agent
            </Typography>
          </div>
          <FormToggleTab
            control={control}
            name="isTrainingAgent"
            errors={errors}
            rules={{ required: false }}
            styles={styles}
            validationMessage={'This field is required'}
            tabOptions={tabOptions}
            isToggleDisabled={isEditDisabled}
          />
        </div>
      </div>
      <ActiveNextMonthWrapper
        showActiveNextMonthToggle={showActiveNextMonthToggle}
        isEditDisabled={isEditDisabled}
        isAgentActiveNextMonth={isAgentActiveNextMonth}
        setIsAgentActiveNextMonth={setIsAgentActiveNextMonth}
        isAgentActiveNextMonthToggleDirty={isAgentActiveNextMonthToggleDirty}
        setIsAgentActiveNextMonthToggleDirty={setIsAgentActiveNextMonthToggleDirty}
      />
      <div className={styles.inputContainer}>
        <AgentFormTextbox
          control={control}
          name="agentName"
          rules={{ required: true }}
          errors={errors}
          isEditDisabled={isEditDisabled || isEditPanDetailsDisabled}
          styles={styles}
          inputLabel={'Agent Name'}
          placeholder={'Enter here'}
          validationMessage={VALIDATION_MESSAGE.AGENT_NAME_MESSAGE}
        />
      </div>

      <div className={styles.inputContainer}>
        <AgentFormDateInput
          control={control}
          name={'dateOfBirth'}
          rules={{ required: false }}
          errors={errors}
          placeholder={'Select date'}
          inputLabel={'Date of birth (as per PAN)'}
          isEditDisabled={isEditDisabled || isEditPanDetailsDisabled}
          styles={styles}
          validationMessage={''}
          showRequiredIcon={false}
          defaultSelected={null}
        />
      </div>

      <div className={styles.inputContainer}>
        <AgentFormTextbox
          control={control}
          name="employeeId"
          rules={{ required: getAgencyCode() === NAVI_AGENCY_CODE }}
          errors={errors}
          isEditDisabled={isEditDisabled}
          styles={styles}
          inputLabel={'Employee ID'}
          placeholder={'Enter here'}
          validationMessage={VALIDATION_MESSAGE.AGENT_EMPLOYEE_ID_MESSAGE}
          isRequired={getAgencyCode() === NAVI_AGENCY_CODE}
        />
      </div>

      <div className={styles.inputContainer}>
        <AgentFormTextbox
          control={control}
          name="emailAddress"
          rules={{
            required: isEmailRequired(),
            validate: (value: string) => {
              if (watch('emailAddress')) {
                return validateEmail(value, isNaviEmailRequired);
              }
            }
          }}
          isRequired={isEmailRequired()}
          inputLabel={'Email ID'}
          placeholder={isEditDisabled && agentData.emailAddress === null ? 'NA' : 'Enter here'}
          isEditDisabled={isEditDisabled || isEmailChangeDisabled}
          errors={errors}
          styles={styles}
          validationMessage={
            isNaviEmailRequired
              ? VALIDATION_MESSAGE.NAVI_EMAIL_MESSAGE
              : VALIDATION_MESSAGE.EMAIL_MESSAGE
          }
        />
      </div>

      <div className={styles.inputContainer}>
        <AgentFormTextbox
          control={control}
          name="phoneNumber"
          rules={{
            required: true,
            minLength: 10,
            maxLength: 10,
            pattern: /^[0-9]{10}$/i
          }}
          inputLabel={'Phone Number'}
          placeholder={'Enter here'}
          isEditDisabled={isEditDisabled}
          errors={errors}
          styles={styles}
          validationMessage={VALIDATION_MESSAGE.PHONE_NUMBER_MESSAGE}
        />
      </div>

      <CorporateNumbersDrawer isEditDisabled={isEditDisabled} agentData={agentData} />

      <div className={styles.inputContainer}>
        <AgentFormTextbox
          control={control}
          name="panNumber"
          rules={{
            required: true,
            pattern: /^[a-zA-Z0-9]{10}$/
          }}
          errors={errors}
          isEditDisabled={isEditDisabled || isEditPanDetailsDisabled}
          styles={styles}
          inputLabel={'PAN'}
          placeholder={'Enter PAN'}
          validationMessage={VALIDATION_MESSAGE.PAN_CARD_MESSAGE}
          isRequired={true}
          isUpperCaseRequired={true}
        />
      </div>

      {watch('panCardView') ? (
        <div className="w-[364px] ml-2 mt-4">
          <AgentFormDownloadPan
            control={control}
            name={'panCardView'}
            rules={{ required: true }}
            isEditDisable={isEditDisabled || isEditPanDetailsDisabled}
            inputLabel={'PAN card'}
            panVerificationStatus={panVerificationStatus}
            isRequired={true}
          />
        </div>
      ) : (
        <AgentFormUploadControl
          control={control}
          name={'panCard'}
          rules={{ required: true }}
          errors={errors}
          isEditDisabled={isEditDisabled || isEditPanDetailsDisabled}
          styles={{}}
          setSubmitButtonDisabled={setIsSubmitButtonDisabled}
          inputLabel={'PAN card'}
          documentType={'PAN_CARD'}
          documentContentType={'JPG'}
          supportedFile={'.jpeg, .jpg, .png'}
          isRequired={true}
        />
      )}

      <div className={styles.inputContainer}>
        <AgentFormDropdown
          control={control}
          name={'bucketGroup'}
          rules={{ required: true }}
          errors={errors}
          placeholder={'Select bucket group'}
          inputLabel={'Bucket Group'}
          isEditDisabled={isEditDisabled}
          styles={styles}
          validationMessage={VALIDATION_MESSAGE.BUCKET_GROUP_MESSAGE}
          options={convertToDropdownOptions(BUCKET_GROUPS)}
          defaultSelected={convertStringToDropdownOption(getValues('bucketGroup'))}
        />
      </div>

      <div className={styles.inputContainer}>
        <AgentFormAutocompleteDropdown
          control={control}
          name={'reportingManagerEmailAddress'}
          inputLabel={'Reporting Manager'}
          rules={{ required: true }}
          errors={errors}
          placeholder={'Type or Select reporting manager'}
          isEditDisabled={isEditDisabled}
          styles={styles}
          options={managerList}
          validationMessage={VALIDATION_MESSAGE.REPORTING_MANAGER_MESSAGE}
          updateClearAllCallback={() => {
            setValue('reportingManagerEmailAddress', '');
          }}
        />
      </div>

      <AgentBaseLocation
        latitude={'currentMonthLatitude'}
        longitude={'currentMonthLongitude'}
        control={control}
        errors={errors}
        isEditDisabled={true}
      />

      {watch('draCertificateView') ? (
        <div className="w-[364px] ml-2 mt-4">
          <AgentFormDownloadDraCertificate
            control={control}
            name={'draCertificateView'}
            rules={{ required: false }}
            isEditDisable={isEditDisabled || !isDraUploadAccessFeatureFlagEnabled}
          />
        </div>
      ) : (
        <div>
          {isDraUploadAccessFeatureFlagEnabled && !isEditDisabled ? (
            <AgentFormUploadControl
              control={control}
              name={'draCertificate'}
              rules={{ required: false }}
              errors={errors}
              isEditDisabled={false}
              styles={{}}
              setSubmitButtonDisabled={setIsSubmitButtonDisabled}
            />
          ) : null}
        </div>
      )}

      <div className={'pt-4 pl-5 flex mt-4'}>
        <Typography variant={'h4'} color="var(--navi-color-gray-c2)" className="pr-1">
          Agent Allocation Details
        </Typography>
        <Typography variant="p4" color="var(--navi-color-gray-c3)">
          (for next month)
        </Typography>
      </div>

      <div className={styles.inputContainer}>
        <AgentFormRadioButton
          control={control}
          name={'topAgent'}
          rules={{ required: false }}
          errors={errors}
          isEditDisabled={isEditDisabled}
          styles={styles}
          inputLabel={'Top Agent'}
          defaultValue={
            agentData?.agentAllocationProfileView?.currentMonthAllocationView?.metadata
              ?.agentPerformanceType || ''
          }
          showRequiredIcon={false}
        />
      </div>

      <div className={styles.inputContainer}>
        <AgentFormAutocompleteDropdown
          control={control}
          name={'agentCity'}
          inputLabel={'Agent city'}
          rules={{ required: false }}
          errors={errors}
          placeholder={'Type or Select city'}
          isEditDisabled={isEditDisabled}
          styles={styles}
          options={convertToDropdownOptions(AGENT_CITY)}
          updateClearAllCallback={() => {
            setValue('agentCity', '');
          }}
          callbackFn={handleAgentCityChange}
          showRequiredIcon={false}
        />
      </div>

      {isCityMismatch && (
        <WarningSection
          message={VERIFICATION_MESSAGE.MISMATCHED_CITY}
          color={'var(--grayscale-2)'}
          iconColor={'var(--grayscale-2)'}
          showTriangle={false}
          bgColor="var(--blue-bg)"
        />
      )}

      <AgentBaseLocation
        latitude={'nextMonthLatitude'}
        longitude={'nextMonthLongitude'}
        control={control}
        errors={errors}
        isEditDisabled={isEditDisabled}
        callbackFn={handleCoordinatesChange}
        showCurrentMonthLabel={false}
      />

      <VerificationComponent
        getValues={getValues}
        locationVerificationStatus={locationVerificationStatus}
        showLocationVerificationToast={showLocationVerificationToast}
        isEditDisabled={isEditDisabled}
        reverseGeocodedAgentCity={reverseGeocodedAgentCity}
        reverseGeocodedAgentPincode={reverseGeocodedAgentPincode}
        handleVerification={handleCityVerification}
        showVerifyButton={showVerifyButton}
        showCityPincode={showCityPincode}
        isMisMatchedState={isMisMatchedCity}
        warningMessage={VERIFICATION_MESSAGE.MISMATCHED_CITY_LOCATION}
      />
    </>
  );
};

export default InternalFieldAgentForm;
